import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  QrCodeScanner as QrCodeScannerIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../../utils/axios';
import { Task } from '../../types';

const QRScannerNew: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdTask, setCreatedTask] = useState<Task | null>(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const qrScanner = useRef<Html5QrcodeScanner | null>(null);

  useEffect(() => {
    if (id) {
      handleScanQRCode(id);
    }
    return () => {
      if (qrScanner.current) {
        qrScanner.current.clear();
      }
    };
  }, [id]);

  const handleScanQRCode = async (qrCodeId: string) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      console.log('Scanning QR code with ID:', qrCodeId);
      const response = await axiosInstance.post(`/qr-codes/${qrCodeId}/scan`);
      
      setSuccess(true);
      setCreatedTask(response.data.task);
      setOpenSuccessDialog(true);
      setError(null);
      stopScanner();
    } catch (error: any) {
      console.error('Error scanning QR code:', error);
      setError(error.response?.data?.message || 'Error al escanear el código QR');
      setSuccess(false);
      stopScanner();
    } finally {
      setLoading(false);
    }
  };

  const handleQrCodeDetected = (decodedText: string) => {
    try {
      let qrCodeId;
      try {
        // Intentar extraer el ID del formato URL completo
        const url = new URL(decodedText);
        const pathParts = url.pathname.split('/');
        qrCodeId = pathParts[pathParts.length - 1];
      } catch (e) {
        // Si no es una URL, intentar extraer el ID directamente
        const parts = decodedText.split('/');
        qrCodeId = parts[parts.length - 1];
        // Si el texto completo parece ser un UUID, usarlo directamente
        if (decodedText.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          qrCodeId = decodedText;
        }
      }

      if (qrCodeId) {
        // Detener el escáner antes de procesar
        stopScanner();
        handleScanQRCode(qrCodeId);
      } else {
        setError('QR inválido: no se pudo extraer el ID');
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      setError('Error al procesar el código QR. Por favor, inténtalo de nuevo.');
    }
  };

  const startScanner = async () => {
    try {
      setError(null);
      const qrRegionId = 'html5-qr-reader';

      // Crear el contenedor del escáner si no existe
      let qrRegion = document.getElementById(qrRegionId);
      if (!qrRegion) {
        qrRegion = document.createElement('div');
        qrRegion.id = qrRegionId;
        const scannerContainer = document.getElementById('scanner-container');
        if (scannerContainer) {
          scannerContainer.appendChild(qrRegion);
        }
      }

      // Configuración del escáner QR con cámara trasera
      const config: any = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        supportedScanTypes: [0], // 0 = Solo cámara (sin archivos)
        videoConstraints: {
          facingMode: { ideal: 'environment' } // Cámara trasera preferida
        }
      };

      qrScanner.current = new Html5QrcodeScanner(
        qrRegionId,
        config,
        false
      );

      setScanning(true);

      qrScanner.current.render(
        (decodedText: string) => {
          console.log('QR Code detected:', decodedText);
          handleQrCodeDetected(decodedText);
        },
        (errorMessage: string) => {
          if (errorMessage.includes('NotFoundException')) return;
          console.error('QR Code scan error:', errorMessage);
        }
      );
    } catch (error: any) {
      console.error('Error starting scanner:', error);
      setError('Error al iniciar el escáner: ' + error.message);
      setScanning(false);
    }
  };

  const stopScanner = () => {
    if (qrScanner.current) {
      try {
        qrScanner.current.clear();
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
      qrScanner.current = null;
    }
    setScanning(false);
  };

  const handleViewTask = () => {
    if (createdTask) {
      navigate(`/tasks/${createdTask.id}`);
    }
  };

  const handleCreateAnother = () => {
    setOpenSuccessDialog(false);
    setSuccess(false);
    setCreatedTask(null);
    setError(null);
    startScanner();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        p={3}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" mt={2}>
          Procesando código QR...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '80vh', p: { xs: 2, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto' }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <QrCodeScannerIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom align="center">
            Escanear Código QR
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" mb={4}>
            Escanea un código QR para crear una tarea automáticamente
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Instrucciones:</strong>
              <br />
              • Haz clic en &quot;Activar Cámara&quot; y permite el acceso cuando el navegador lo solicite
              <br />
              • Apunta la cámara hacia un código QR generado desde &quot;Gestión de QR&quot;
              <br />
              • El escáner detectará automáticamente el código y creará la tarea
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
              {error}
              <Box mt={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setError(null);
                    startScanner();
                  }}
                >
                  Reintentar
                </Button>
              </Box>
            </Alert>
          )}

          <Box
            id="scanner-container"
            sx={{
              width: '100%',
              maxWidth: '500px',
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {!scanning && !loading && (
              <Button
                variant="contained"
                size="large"
                startIcon={<QrCodeScannerIcon />}
                onClick={startScanner}
                fullWidth
              >
                Activar Cámara para Escanear
              </Button>
            )}

            {scanning && (
              <Button
                variant="outlined"
                onClick={stopScanner}
                fullWidth
                sx={{ mt: 2 }}
              >
                Detener Escáner
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={openSuccessDialog}
        onClose={() => setOpenSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
            <Typography variant="h6">¡Tarea Creada Exitosamente!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {createdTask && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Servicio:</strong> {createdTask.location}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Cliente:</strong> {createdTask.company?.name || createdTask.individual?.name || 'N/A'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Categoría:</strong> {createdTask.category?.name || 'N/A'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Responsable:</strong> {createdTask.responsible ? `${createdTask.responsible.firstName} ${createdTask.responsible.lastName}` : 'Sin asignar'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Estado:</strong> {createdTask.status === 'PENDING' ? 'Pendiente' : createdTask.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/tasks')} variant="outlined">
            Ver Todas las Tareas
          </Button>
          <Button onClick={handleViewTask} variant="contained" color="primary">
            Ver Tarea
          </Button>
          <Button onClick={handleCreateAnother} variant="contained" color="success">
            Escanear Otro QR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QRScannerNew;
